import { BookingState } from '../model/bookingTypes'

export function clearState() {
  sessionStorage.removeItem('bookingState')
}

// Clear all booking-related storage (sessionStorage + all localStorage keys for a given slug)
export function clearAllBookingStorage(meetingLinkSlug?: string) {
  if (typeof window === 'undefined') return

  sessionStorage.removeItem('bookingState')

  // Remove specific keys for this meeting link
  if (meetingLinkSlug) {
    localStorage.removeItem(`bookingState_${meetingLinkSlug}`)
    localStorage.removeItem(`bookingState_backup_${meetingLinkSlug}`)
  }
  localStorage.removeItem('bookingState_default')
  localStorage.removeItem('bookingState_backup_default')

  // Remove all step-specific keys
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i)
    if (key && key.startsWith('bookingState_step_')) {
      if (!meetingLinkSlug || key.endsWith(meetingLinkSlug) || key.endsWith('default')) {
        localStorage.removeItem(key)
      }
    }
  }
}

// Helper function to load state from storage
export function loadStateFromStorage(state: BookingState): BookingState | null {
  if (typeof window === 'undefined') return null

  try {
    // Try to get state from session storage first
    const sessionState = sessionStorage.getItem('bookingState')
    let loadedState = sessionState ? JSON.parse(sessionState) : null

    // If no session state, try to load from localStorage backup
    if (!loadedState && state.meetingLinkSlug) {
      const backupKey = `bookingState_backup_${state.meetingLinkSlug}`
      const localState = localStorage.getItem(backupKey)
      loadedState = localState ? JSON.parse(localState) : null
    }

    // Check if state exists and isn't expired
    if (loadedState && loadedState._meta) {
      // Check if state is expired
      if (new Date(loadedState._meta.expiresAt) < new Date()) {
        console.log('Booking state has expired, starting fresh')
        return state // Return current state if expired
      }

      // Verify essential fields are present
      const requiredFields = ['meetingLinkSlug', 'currentStep']
      const isValid = requiredFields.every((field) => loadedState[field] !== undefined)

      if (!isValid) {
        console.log('Stored state is missing required fields')
        return state // Return current state if invalid
      }

      // Clear the metadata before returning
      const { _meta, ...cleanState } = loadedState
      return cleanState as BookingState
    }

    return loadedState ? (loadedState as BookingState) : state
  } catch (error) {
    console.error('Error loading booking state from storage:', error)
    return state // Return current state on error
  }
}

// Function to save state to storage
export function saveStateToStorage(state: BookingState) {
  if (typeof window === 'undefined') {
    return
  }

  try {
    // Add metadata to the stored state
    const stateWithMetadata = {
      ...state,
      _meta: {
        timestamp: new Date().toISOString(),
        version: '1.0',
        lastPath: typeof window !== 'undefined' ? window.location.pathname : '',
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // 24 hours
      },
    }

    // Save to session storage for normal operation
    sessionStorage.setItem('bookingState', JSON.stringify(stateWithMetadata))

    // Create a predictable key for localStorage backup with meetingLinkSlug
    const backupKey = `bookingState_backup_${state.meetingLinkSlug || 'default'}`
    localStorage.setItem(backupKey, JSON.stringify(stateWithMetadata))

    // Also save a version by current step for more granular recovery
    if (state.currentStep) {
      const stepKey = `bookingState_step_${state.currentStep}_${state.meetingLinkSlug || 'default'}`
      localStorage.setItem(stepKey, JSON.stringify(stateWithMetadata))
    }
  } catch (error) {
    console.error('Error saving booking state to storage:', error)
  }
}

// Function to clean up old sessions
export function cleanupOldSessions() {
  if (typeof window === 'undefined') return

  try {
    // Get current time
    const now = new Date()

    // Check all localStorage items for expired booking states
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && (key.startsWith('bookingState_backup_') || key.startsWith('bookingState_step_'))) {
        try {
          const value = localStorage.getItem(key)
          if (value) {
            const parsedValue = JSON.parse(value)
            // Check if it has _meta with expiresAt
            if (parsedValue._meta && parsedValue._meta.expiresAt) {
              const expiresAt = new Date(parsedValue._meta.expiresAt)
              if (expiresAt < now) {
                // This session has expired, remove it
                console.log(`Removing expired session: ${key}`)
                localStorage.removeItem(key)
              }
            } else if (parsedValue._meta && parsedValue._meta.timestamp) {
              // If there's a timestamp but no expiresAt, check if it's older than 24 hours
              const timestamp = new Date(parsedValue._meta.timestamp)
              const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000)
              if (timestamp < twentyFourHoursAgo) {
                console.log(`Removing old session: ${key}`)
                localStorage.removeItem(key)
              }
            }
          }
        } catch (error) {
          // If it can't be parsed, it's probably corrupted
          console.error(`Error parsing booking state for key ${key}:`, error)
          localStorage.removeItem(key)
        }
      }
    }
  } catch (error) {
    console.error('Error cleaning up old booking sessions:', error)
  }
}
